// The template pages' cards (template-page spec §3): General, Look (the four
// small groups that make up a look, as subheadings) and Custom CSS, all bound
// to one form through `control`. A card holds one decision and everything
// that shapes it; the Look groups are each about one field, which is why
// they share a card instead of each being a section with its own nav entry.

import { Input, type SectionNavItem, Select } from "@pej/kore/components";
import type { ReactNode } from "react";
import type { Control, FieldPath } from "react-hook-form";
import { BrandColorField } from "@/components/brand-color-field";
import { CssEditor } from "@/components/css-editor";
import { SectionCard } from "@/components/section-page/section-card";
import { PreviewPanel } from "@/features/preview/preview-panel";
import { Field } from "@/lib/forms";
import { ArtworkSlot } from "./artwork-field";
import { SAMPLE_STEPS, SAMPLE_SURVEY_NAME, SAMPLE_THANK_YOU } from "./sample-survey";
import { FONT_LABELS, type TemplateValues, toDraftTemplate } from "./template-schema";

export const TEMPLATE_SECTION = {
    general: "section-general",
    look: "section-look",
    css: "section-css",
    usedBy: "section-used-by",
    danger: "section-danger",
    history: "section-history",
} as const;

/** The nav for a saved template (`existing`) or a new one (no Used by, Danger zone or History). */
export function templateNavItems(existing: boolean): SectionNavItem[] {
    return [
        { id: TEMPLATE_SECTION.general, label: "General" },
        { id: TEMPLATE_SECTION.look, label: "Look" },
        { id: TEMPLATE_SECTION.css, label: "Custom CSS" },
        ...(existing
            ? [
                  { id: TEMPLATE_SECTION.usedBy, label: "Used by" },
                  { id: TEMPLATE_SECTION.danger, label: "Danger zone" },
                  { id: TEMPLATE_SECTION.history, label: "History" },
              ]
            : []),
    ];
}

interface SectionProps {
    control: Control<TemplateValues>;
}

interface ColorFieldProps {
    name: FieldPath<TemplateValues>;
    /** Role caption rendered inside the control (vouchers-style). */
    caption: string;
    control: Control<TemplateValues>;
    description?: string;
}

/** One brand-color box: swatch, role, and editable hex in a single control. */
function ColorField({ name, caption, control, description }: ColorFieldProps) {
    return (
        <Field
            name={name}
            control={control}
            description={description}
            render={({ field: { value, onChange, onBlur } }) => (
                <BrandColorField caption={caption} value={String(value)} onChange={onChange} onBlur={onBlur} />
            )}
        />
    );
}

function Subsection({ title, description, children }: { title: string; description: string; children: ReactNode }) {
    return (
        <div className="flex flex-col gap-4 border-t border-kore-border pt-5 first:border-t-0 first:pt-0">
            <div>
                <h3 className="text-sm font-semibold">{title}</h3>
                <p className="mt-1 text-sm text-kore-text-muted">{description}</p>
            </div>
            {children}
        </div>
    );
}

export function GeneralCard({ control }: SectionProps) {
    return (
        <SectionCard id={TEMPLATE_SECTION.general} title="General">
            <Field name="name" control={control} label="Name" required render={({ field }) => <Input {...field} />} />
        </SectionCard>
    );
}

export function LookCard({ control, fontChoice }: SectionProps & { fontChoice: string }) {
    return (
        <SectionCard id={TEMPLATE_SECTION.look} title="Look">
            <div className="flex flex-col gap-5">
                <Subsection title="Background" description="The surface behind everything. An image covers the colour.">
                    <ColorField name="backgroundColor" caption="Background" control={control} />
                    <Field
                        name="backgroundImageAssetId"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <ArtworkSlot
                                value={String(value)}
                                onChange={onChange}
                                fieldLabel="Background image"
                                pickerTitle="Choose a background"
                                unavailableLabel="Background image"
                            />
                        )}
                    />
                </Subsection>
                <Subsection title="Logo" description="Your mark, above the question.">
                    <Field
                        name="logoAssetId"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <ArtworkSlot
                                value={String(value)}
                                onChange={onChange}
                                fieldLabel="Logo image"
                                pickerTitle="Choose a logo"
                                unavailableLabel="Logo image"
                            />
                        )}
                    />
                    <Field
                        name="logoPosition"
                        control={control}
                        label="Position"
                        render={({ field: { value, onChange, name } }) => (
                            <Select value={value} onValueChange={onChange} name={name}>
                                <Select.Trigger>
                                    <Select.Value>
                                        {(v: unknown) => (v === "left" ? "Left" : v === "right" ? "Right" : "Center")}
                                    </Select.Value>
                                </Select.Trigger>
                                <Select.Content>
                                    <Select.Item value="left">Left</Select.Item>
                                    <Select.Item value="center">Center</Select.Item>
                                    <Select.Item value="right">Right</Select.Item>
                                </Select.Content>
                            </Select>
                        )}
                    />
                </Subsection>
                <Subsection title="Type" description="The typeface every line of text is set in.">
                    <Field
                        name="fontChoice"
                        control={control}
                        label="Font"
                        description="Named fonts render only where the guest's device has them; uploads always work."
                        render={({ field: { value, onChange, name } }) => (
                            <Select value={value} onValueChange={onChange} name={name}>
                                <Select.Trigger>
                                    {/* The fallback is unreachable — fontChoiceFor only ever returns a
                                        key in this map — but Inter is what an unknown stack renders. */}
                                    <Select.Value>
                                        {(v: unknown) => FONT_LABELS[String(v)] ?? FONT_LABELS.inter}
                                    </Select.Value>
                                </Select.Trigger>
                                <Select.Content>
                                    {Object.entries(FONT_LABELS).map(([key, label]) => (
                                        <Select.Item key={key} value={key}>
                                            {label}
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select>
                        )}
                    />
                    {fontChoice === "custom" && (
                        <Field
                            name="customFontStack"
                            control={control}
                            label="Custom font"
                            description="The fonts to try, best first — the widget uses the first one it can load."
                            render={({ field }) => <Input {...field} placeholder='"Brand Sans", sans-serif' />}
                        />
                    )}
                </Subsection>
                <Subsection
                    title="Colours"
                    description="Text is every line of copy. Accent picks out the choice icons, the selected chips and the Send button."
                >
                    <div className="grid gap-4 sm:grid-cols-2">
                        <ColorField name="slotText" caption="Text" control={control} />
                        <ColorField name="slotAccent" caption="Accent" control={control} />
                    </div>
                </Subsection>
            </div>
        </SectionCard>
    );
}

export function CssCard({ control }: SectionProps) {
    return (
        <SectionCard id={TEMPLATE_SECTION.css} title="Custom CSS">
            <p className="text-sm text-kore-text-muted">
                Optional styling for anything the fields above don't cover, shared by every survey using this template.
                The examples are commented out — uncomment one to start.
            </p>
            <Field
                name="customCss"
                control={control}
                label="Template CSS"
                optional
                render={({ field: { value, onChange } }) => (
                    <CssEditor value={String(value)} onChange={onChange} ariaLabel="Template CSS" />
                )}
            />
        </SectionCard>
    );
}

/** The template pages' preview: fixed sample content in the draft look. A
 *  template has no survey of its own, and the sample exercises every element
 *  a template can style. Nothing to follow or play. */
export function TemplatePreview({ values }: { values: TemplateValues }) {
    return (
        <PreviewPanel
            surveyName={SAMPLE_SURVEY_NAME}
            thankYouText={SAMPLE_THANK_YOU}
            language="en"
            steps={SAMPLE_STEPS}
            template={toDraftTemplate(values)}
            playable={false}
        />
    );
}
